export const HOT_STAMP = 'Hot-Stamp';
export const TROCA_OLEO = 'Troca de óleo';
export const ACRILICO = 'Acrilico';
export const ESTAMPARIA = 'Estamparia';

export const ROTULO_MATERIAL: Record<string, string> = {
  [ACRILICO]: 'Acrílico',
  [ESTAMPARIA]: 'Silk (estamparia)',
};
export const DESCONTO_PIX = 0.1;

export interface MaterialOpcoes {
  nome: string;
  produtos: string[];
  quantidades: number[];
  impressao: string[];
  corte: string[];
  acabamentos: string[];
}

export interface MedidaOleo {
  nome: string;
  dimensoes: { largura: number; altura: number } | null;
  corte: string | null;
  modelos: string[];
}

export interface TipoOleo {
  nome: string;
  descricao: string | null;
  impressao: string | null;
  acabamentoFixo: string | null;
  medidas: MedidaOleo[];
}

export interface OpcoesCotacao {
  materiais: MaterialOpcoes[];
  hotStamp: { tamanhos: string[]; impressao: string[]; quantidades: number[] } | null;
  trocaOleo: {
    tipos: TipoOleo[];
    acabamentos: string[];
    cortes: string[];
    quantidades: number[];
    minimo: number;
  } | null;
  acrilico: {
    larguras: number[];
    tipos: string[];
    espessurasTransparente: string[];
    cores: Record<string, string[]>;
    acabamentos: string[];
    gabaritos: string[];
  } | null;
  estamparia: {
    produtos: string[];
    locais: { id: string; nome: string; tipo: string; perna: boolean }[];
    tamanhos: string[];
  } | null;
  urlTabela: string | null;
}

export interface ResultadoCotacao {
  total: number;
  unitario: number;
  alternativas: { qtd: number; total: number }[];
  pix?: number;
  descontoPix?: number;
  itens?: { indice?: number; desc?: string; qtd?: number; unitario: number; valor: number }[];
}

export const brl = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

export const porUnidade = (v: number) =>
  v.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: v < 1 ? 3 : 2,
    maximumFractionDigits: v < 1 ? 3 : 2,
  });

export const valorPix = (total: number, frete: number) =>
  (total - frete) * (1 - DESCONTO_PIX) + frete;

export function resumoCotacao(p: {
  material: string;
  produto?: string;
  qtd: number;
  medida: string;
  detalhes: string[];
  frete: number;
  resultado: ResultadoCotacao;
}): string {
  const nome = [p.material, p.produto].filter(Boolean).join(' · ');
  return [
    `*${nome}*`,
    `${p.qtd.toLocaleString('pt-BR')} un · ${p.medida}`,
    ...p.detalhes.filter(Boolean).map((x) => `• ${x}`),
    '',
    `Total: *${brl(p.resultado.total)}* (${porUnidade(p.resultado.unitario)}/un)`,
    `No Pix: *${brl(p.resultado.pix ?? valorPix(p.resultado.total, p.frete))}*`,
    ...(p.frete > 0 ? [`Frete incluso: ${brl(p.frete)}`] : []),
  ].join('\n');
}

export function medidasEmCm(tamanho: string | null): { largura: number; altura: number } | null {
  const t = tamanho ?? '';
  const emCm = (v: string, unidade?: string) => {
    const fator = { mm: 0.1, cm: 1, m: 100 }[(unidade ?? 'cm').toLowerCase() as 'mm' | 'cm' | 'm'];
    return Math.round(Number(v.replace(',', '.')) * fator * 100) / 100;
  };
  const m = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*(mm|cm|m)?\b/i.exec(t);
  if (m) return { largura: emCm(m[1]!, m[3]), altura: emCm(m[2]!, m[3]) };
  // uma medida só ("4 cm (redondo)", "Ø 5cm", "50 mm de diâmetro"): redondo ou quadrado desse tamanho
  const um = /(\d+(?:[.,]\d+)?)\s*(mm|cm|m)\b/i.exec(t);
  if (!um) return null;
  const lado = emCm(um[1]!, um[2]);
  return { largura: lado, altura: lado };
}

export function materialProvavel(texto: string): string | null {
  const t = texto.toLowerCase();
  if (/hot.?stamp/.test(t)) return HOT_STAMP;
  if (/troca|[óo]leo/.test(t)) return TROCA_OLEO;
  if (/acr[íi]lic/.test(t)) return ACRILICO;
  if (!/adesivo/.test(t) && /silk|estamp|camiseta|uniforme|ecobag/.test(t)) return ESTAMPARIA;
  if (/lacre|void|destrut|casca|hologr/.test(t)) return 'Vinil Destrutível';
  if (/couch/.test(t)) return 'Couché Adesivo';
  if (/lona|banner/.test(t)) return 'Lona';
  if (/adesivo|vinil/.test(t)) return 'Vinil Adesivo';
  return null;
}

const PISTAS_PRODUTO: [RegExp, RegExp][] = [
  [/casca/, /importado/],
  [/void.*branc|branc.*void/, /void branco/],
  [/void.*alum|alum/, /void alum/],
  [/void/, /void prata/],
  [/\b3m\b/, /^3m$/],
  [/\bap\b/, /^ap$/],
  [/hologr/, /hologr/],
  [/transparent/, /transparente/],
  [/gold|dourad/, /gold|dourad/],
  [/prata/, /prata/],
  [/fosc/, /fosc/],
  [/brilh/, /brilh/],
];

export function produtoProvavel(texto: string, produtos: string[]): string {
  const t = texto.toLowerCase();
  for (const [noTexto, noProduto] of PISTAS_PRODUTO) {
    if (!noTexto.test(t)) continue;
    const achado = produtos.find((p) => noProduto.test(p.toLowerCase()));
    if (achado) return achado;
  }
  return produtos[0] ?? '';
}

export const EVENTO_COLAR = 'viniko:colar-mensagem';

export function colarNaMensagem(conversaId: string, texto: string) {
  window.dispatchEvent(new CustomEvent(EVENTO_COLAR, { detail: { conversaId, texto } }));
}
